import { FC } from 'react';
import { Listbox, Switch } from '@headlessui/react';
import { isNil, isNumber, range } from 'lodash';
import moment from 'moment';
import { Controller, UseFormSetValue } from 'react-hook-form';
import { ReactComponent as DropdownIcon } from 'assets/icons/chevron-down.svg';
import { ReactComponent as CloseIcon } from 'assets/icons/add_circle_outline.svg';
import { CreateOrEditServiceSubmitData } from '../CreateOrEditService';
import { ReactComponent as InfoIcon } from 'assets/icons/info.svg';
import ReactTooltip from 'react-tooltip';
import useClinicSchedule from 'hooks/useClinicSchedule';
import useClinicException from 'hooks/useClinicException';
import { WORKING_HOUR_EXCEPTION } from 'utils/constants';
import useClinic from 'hooks/useClinic';

interface BlockSchedulingSectionProps {
  control: any;
  setValue: UseFormSetValue<CreateOrEditServiceSubmitData>;
  blockSchedulingStartAt?: string | null;
  defaultIgnoreBlockSchedulingWithin?: number | null;
}
const OPTIONS = range(1, 8, 1).map((number) => ({
  label: `${number} working day${number > 1 ? 's' : ''}`,
  value: number,
}));

const OPTIONS_RANGE = [{ label: 'Same Day', value: 0 }, ...OPTIONS];

const BlockSchedulingSection: FC<BlockSchedulingSectionProps> = ({
  control,
  blockSchedulingStartAt,
  defaultIgnoreBlockSchedulingWithin,
  setValue,
}) => {
  const { data: clinic, isLoading: isClinicLoading } = useClinic();
  const { data: clinicSchedule, isLoading: isClinicScheduleLoading } =
    useClinicSchedule();

  const { data: clinicException, isLoading: isClinicExceptionLoading } =
    useClinicException({
      startDate: moment().tz(clinic.timezone).add(1, 'd').format('YYYY-MM-DD'),
      endDate: moment().tz(clinic.timezone).add(2, 'y').format('YYYY-MM-DD'),
      shouldRun: !!clinicSchedule && !!clinic,
      source: clinicSchedule ? clinicSchedule.currentWorkingHour : '',
    });

  const isLoading =
    isClinicScheduleLoading || isClinicExceptionLoading || isClinicLoading;

  const getLabelContent = (currentIgnoreBlockSchedulingWithin: number) => {
    if (isNil(currentIgnoreBlockSchedulingWithin) && !blockSchedulingStartAt) {
      return null;
    }

    if (
      currentIgnoreBlockSchedulingWithin === defaultIgnoreBlockSchedulingWithin
    ) {
      return `Applies to
      ${moment(blockSchedulingStartAt).format('DD MMM YYYY')}
      `;
    }

    if (currentIgnoreBlockSchedulingWithin === 0) {
      const currentDate = moment().tz(clinic.timezone).format('DD MMM YYYY');

      return `Applies to the current date (${currentDate})`;
    }

    if (currentIgnoreBlockSchedulingWithin) {
      return `Applies from
      ${moment().tz(clinic.timezone).format('DD MMM')} to 
      ${moment(blockSchedulingStartAt).format('DD MMM YYYY')}
      `;
    }
  };

  const onChange = (value: number) => {
    setValue('ignoreBlockSchedulingWithin', value, {
      shouldValidate: true,
      shouldDirty: true,
    });

    if (value === 0) {
      setValue(
        'blockSchedulingStartAt',
        moment.tz(clinic.timezone).format('YYYY-MM-DD'),
        {
          shouldValidate: true,
        }
      );
      return;
    }

    if (!clinicException) return;
    let countedDateLeft = value;
    let nextBlockSchedulingStartAt = null;

    for (let i = 0; i < clinicException.length; i++) {
      const currentDate = clinicException[i];

      const isRegularWorkingHourOff =
        typeof currentDate.workingHour === 'string'
          ? WORKING_HOUR_EXCEPTION.CLOSED
          : currentDate.workingHour.every((item) => !item.start && !item.end);

      const isCurrentDateClosed =
        currentDate.isClosed ||
        currentDate.isHoliday ||
        isRegularWorkingHourOff;

      if (isCurrentDateClosed) {
        continue;
      }

      countedDateLeft -= 1;

      if (countedDateLeft === 0 && !isCurrentDateClosed) {
        nextBlockSchedulingStartAt = currentDate.date;
        break;
      }
    }

    setValue('blockSchedulingStartAt', nextBlockSchedulingStartAt, {
      shouldValidate: true,
    });
  };

  return (
    <Controller
      control={control}
      name="isBlockSchedulingEnabled"
      render={({ field }) => (
        <>
          <div className="flex justify-between items-center">
            <label className="font-bold text-16 leading-[2.4rem]" htmlFor="">
              Block Scheduling Mode
            </label>
            <div className="flex gap-x-1.6 items-center">
              <Switch
                onChange={field.onChange}
                onBlur={field.onBlur}
                checked={field.value}
                className={`${
                  field.value ? 'bg-magenta' : 'bg-[#78788029]'
                } relative inline-flex h-2.4 w-4 items-center rounded-full`}
              >
                <span
                  className={`${
                    field.value ? 'translate-x-[1.8rem]' : 'translate-x-[2px]'
                  } inline-block h-2 w-2 transform rounded-full transition bg-white`}
                />
              </Switch>
              <span className="uppercase">{field.value ? 'On' : 'Off'}</span>
            </div>
          </div>
          {field.value && (
            <Controller
              name={'ignoreBlockSchedulingWithin'}
              control={control}
              render={({ field }) => (
                <div className="flex flex-col gap-y-0.8">
                  <div className="flex items-start gap-x-0.8">
                    <h3 className="text-darkest-grey text-16 leading-[2.4rem] font-bold">
                      Ignore Block Scheduling Within Timeframe
                    </h3>
                    <button data-for="service-block-scheduling" data-tip="">
                      <InfoIcon className="w-1.6 h-1.6 all-child:fill-mid-grey" />
                    </button>
                    <ReactTooltip
                      id="service-block-scheduling"
                      effect="solid"
                      place="bottom"
                      className="!bg-white !rounded-[0.8rem] !w-[18.5rem] !h-[auto] z-[9999] flex justify-center items-center !opacity-100 shadow-elevation-07 after:hidden before:hidden !px-[10px] !py-[4px]"
                    >
                      <span className="text-11 text-darkest-grey flex items-center font-normal">
                        This setting will cause block scheduling to be ignored
                        for the first x days.
                      </span>
                    </ReactTooltip>
                  </div>
                  <Listbox {...field} onChange={onChange} disabled={isLoading}>
                    {({ open }) => {
                      return (
                        <div className="relative">
                          <Listbox.Button
                            className={`relative w-full h-[4.8rem] pl-1.6 cursor-pointer rounded-[1rem] bg-white border-[0.5px] border-light-grey text-left shadow-primary hover:border-magenta hover:shadow-input-active outline-none ${
                              open ? 'border-magenta shadow-input-active' : ''
                            }`}
                          >
                            <span
                              className={`block pr-3 ${
                                field.value
                                  ? 'text-darkest-grey'
                                  : 'text-mid-grey'
                              } `}
                            >
                              {isNumber(field.value)
                                ? OPTIONS_RANGE.find(
                                    (option) => option.value === field.value
                                  )!.label
                                : 'Select delay duration'}
                            </span>
                            <span
                              className={`pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2`}
                            >
                              <DropdownIcon className="w-1.4 h-1.4" />
                            </span>
                            {isNumber(field.value) && (
                              <button
                                className="absolute inset-y-0 right-3 flex items-center pr-1.6"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  field.onChange(null);
                                  setValue('blockSchedulingStartAt', null, {
                                    shouldValidate: true,
                                    shouldDirty: true,
                                  });
                                }}
                              >
                                <CloseIcon className="w-1.6 h-1.6 transform rotate-45 all-child:all-child:fill-grey" />
                              </button>
                            )}
                          </Listbox.Button>
                          <Listbox.Options className="absolute mt-0.5 max-h-16 scrollbar w-full overflow-auto rounded-[0.8rem] bg-white py-1.5 text-base shadow-primary flex flex-col gap-y-1.5 z-10">
                            {OPTIONS_RANGE.map(({ label, value }) => (
                              <Listbox.Option
                                key={value}
                                className="relative cursor-default select-none pl-1.6"
                                value={value}
                              >
                                {({ selected }) => (
                                  <span
                                    className={`block truncate cursor-pointer hover:text-magenta ${
                                      selected ? 'text-magenta' : ''
                                    }`}
                                  >
                                    {label}
                                  </span>
                                )}
                              </Listbox.Option>
                            ))}
                          </Listbox.Options>
                        </div>
                      );
                    }}
                  </Listbox>
                  {(isNumber(field.value) || blockSchedulingStartAt) && (
                    <span className="mt-0.8">
                      {getLabelContent(field.value)}
                    </span>
                  )}
                </div>
              )}
            />
          )}
        </>
      )}
    />
  );
};

export default BlockSchedulingSection;
